import React, { useState, useEffect } from 'react';
import { FlexCol, FlexRow } from './generic/Containers';
import { Body1, BCHeader1, BCHeader2, BCHeader3 } from './generic/BCHeaders';
import { BCPrimaryButton } from './generic/BCButtons';
import { BCIcon, ICONS } from './generic/BCIcons';
import { COLORS } from './generic/BCColors';
import { CouponContainer } from './CouponComponent';
import { TESTIMONIALS_CONFIG } from '../constants/TestimonialsConfig';
import { getPricingOptions, createNewCheckoutSession } from '../util/APIHelper';
import { createCheckoutSession } from '../util/Stripe';
import { trackEvent, TRACKING_EVENTS } from '../util/TrackingHelper';
import { useGlobalState } from '../GlobalState';

interface UpdatedPricingOption {
    cssId: string;
    interval: string;
    price: number;
    recommended: boolean;
    stripeId: string;
    title: string;
}

interface PremiumUpsellModalProps {
    title?: string;
    subtitle?: string;
    ctaText?: string;
    onClose?: () => void;
    showAsModal?: boolean;
    source?: string; // For tracking where the modal was opened from
    onSuccess?: () => void; // Called after successful checkout session creation
}

export const PremiumUpsellModal: React.FC<PremiumUpsellModalProps> = ({
    title = "Your Coding Career Starts Here",
    subtitle = "Join thousands of developers who've transformed their careers with Beyond Code",
    ctaText = "Launch My Coding Career",
    onClose,
    showAsModal = false,
    source = 'unknown',
    onSuccess
}) => {
    const { user } = useGlobalState();
    const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('year');
    const [approvedCouponCode, setApprovedCouponCode] = React.useState("");
    const [pricingOptions, setPricingOptions] = useState<any[]>([]);
    const [selectedPlanId, setSelectedPlanId] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingPricing, setIsLoadingPricing] = useState(true);
    
    // @ts-ignore
    const yearlyPlan = pricingOptions.find(opt => opt.interval === 'year');
    // @ts-ignore
    const monthlyPlan = pricingOptions.find(opt => opt.interval === 'month');
    
    // Calculate savings percentage dynamically
    const savingsPercentage = React.useMemo(() => {
        if (monthlyPlan && yearlyPlan) {
            const monthlyCostPerYear = monthlyPlan.price * 12;
            const yearlyCost = yearlyPlan.price;
            return Math.round(((monthlyCostPerYear - yearlyCost) / monthlyCostPerYear) * 100);
        }
        return 0;
    }, [monthlyPlan, yearlyPlan]);

    const transformPricingOptions = (options: UpdatedPricingOption[]) => {
        // Calculate savings for yearly plan
        const monthlyOption = options.find((opt: any) => opt.interval === "month");
        const yearlyOption = options.find((opt: any) => opt.interval === "year");
        
        let savingsPercentage = 0;
        if (monthlyOption && yearlyOption) {
            const yearlyCost = yearlyOption.price;
            const monthlyCost = monthlyOption.price * 12;
            savingsPercentage = Math.round(((monthlyCost - yearlyCost) / monthlyCost) * 100);
        }

        return options.map((option: any) => ({
            label: option.title || "Plan",
            trial: "7-Day Free Trial",
            price: option.price,
            interval: option.interval,
            stripeId: option.stripeId,
            highlight: option.recommended,
            badge: option.interval === 'year' && savingsPercentage > 0 ? `Save ${savingsPercentage}%` : undefined
        }));
    };

    const handlePricingOptionsUpdate = (updatedOptions: UpdatedPricingOption[]) => {
        const transformedOptions = transformPricingOptions(updatedOptions);
        setPricingOptions(transformedOptions);
        const recommendedOption = transformedOptions.find(opt => opt.highlight);
        if (recommendedOption) {
            setSelectedPlanId(recommendedOption.stripeId);
        }
    };

    const handleIntervalChange = (interval: 'month' | 'year') => {
        setBillingInterval(interval);
        const plan = interval === 'year' ? yearlyPlan : monthlyPlan;
        if (plan) {
            setSelectedPlanId(plan.stripeId);
        }
    };

    // Fetch pricing options on mount
    useEffect(() => {
        const fetchPricingOptions = async () => {
            try {
                const response = await getPricingOptions();
                if (response && response.data && response.data.pricingOptions) {
                    const options = response.data.pricingOptions;
                    setPricingOptions(options);
                    
                    // Set the recommended option as default selected
                    const recommendedOption = options.find((opt: any) => opt.recommended);
                    if (recommendedOption) {
                        setSelectedPlanId(recommendedOption.stripeId);
                    }
                }
            } catch (error) {
                console.error("Error fetching pricing options:", error);
            } finally {
                setIsLoadingPricing(false);
            }
        };

        fetchPricingOptions();
    }, []);

    // Update billing interval when selected plan changes
    useEffect(() => {
        if (yearlyPlan && monthlyPlan && selectedPlanId) {
            if (selectedPlanId === yearlyPlan.stripeId) {
                setBillingInterval('year');
            } else if (selectedPlanId === monthlyPlan.stripeId) {
                setBillingInterval('month');
            }
        }
    }, [selectedPlanId, yearlyPlan, monthlyPlan]);

    // Handle checkout process
    const handleStartFreeTrial = async () => {
        if (!user || !selectedPlanId) return;

        setIsLoading(true);

        try {
            trackEvent(TRACKING_EVENTS.TRIAL_STARTED, { 
                user_id: user.uid, 
                plan_type: selectedPlanId,
                source: source
            });
            
            const successUrl = `${window.location.origin}/?payment_success=true&plan=${selectedPlanId}`;
            const cancelUrl = window.location.origin;
            
            const response = await createNewCheckoutSession(
                approvedCouponCode,
                successUrl, 
                cancelUrl, 
                user.uid, 
                user.email, 
                selectedPlanId
            );
            
            if (response && response.data && response.data.sessionId) {
                createCheckoutSession(response.data.sessionId);
                onSuccess?.();
            } else {
                console.error('Failed to create checkout session');
            }
        } catch (error) {
            console.error('Error starting trial:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const content = (
        <FlexCol align="center" gap={1} widthPercent={100}>
            <FlexCol align="center" gap={1} style={{ textAlign: 'center', marginBottom: '1rem' }}>
                <BCHeader1 text={title} />
                <Body1
                    text={subtitle}
                    style={{ fontSize: '1.1rem', opacity: 0.8 }}
                />
            </FlexCol>

            {isLoadingPricing ? (
                <FlexCol align="center" gap={1} style={{ width: '100%' }}>
                    <Body1 text="Loading subscription options..." textAlign="center" color={'TEXT_SECONDARY'}/>
                </FlexCol>
            ) : !yearlyPlan || !monthlyPlan ? (
                <FlexCol align="center" gap={1} style={{ width: '100%' }}>
                    <Body1 text="Unable to load subscription options" textAlign="center" color={"TEXT_SECONDARY"}/>
                </FlexCol>
            ) : (
                <FlexRow gap={2} align="flex-start" widthPercent={100} className="premium-upsell-content">
                    <FlexCol gap={1.5} flex={1} className="pricing-options-column" widthPercent={100}>
                        <CouponContainer setApprovedCouponCode={setApprovedCouponCode} onPricingOptionsUpdate={handlePricingOptionsUpdate}/>
<PricingOptionCard
                                            title="Yearly"
                                            subtitle="7-Day Free Trial"
                                            price={`Then $${Math.round(yearlyPlan.price)}/year`}
                                            isSelected={billingInterval === 'year'}
                                            onClick={() => handleIntervalChange('year')}
                                            badge={savingsPercentage > 0 ? `Save ${savingsPercentage}%` : null}
                                        />

                        <PricingOptionCard
                            title="Monthly"
                            subtitle="7-Day Free Trial"
                            price={`Then $${Math.round(monthlyPlan.price)}/month`}
                            isSelected={billingInterval === 'month'}
                            onClick={() => handleIntervalChange('month')}
                            badge={null}
                        />

                        <FlexCol gap={.5}>
                            {/* CTA Button */}
                            <BCPrimaryButton
                                text={ctaText}
                                onClick={handleStartFreeTrial}
                                disabled={isLoading}
                                iconRight={ICONS.RIGHT_ARROW}
                                style={{
                                    padding: '1rem 2rem',
                                    fontSize: '1.1rem',
                                    fontWeight: '600',
                                    borderRadius: '12px',
                                    marginTop: '1rem',
                                    textAlign: 'center',
                                    justifyContent: "center"
                                }}
                            />

                            <FlexCol>
                                <Body1
                                    text="7-day free trial • Cancel anytime"
                                    textAlign="center"
                                    color="TEXT_SECONDARY"
                                    fontSize={0.8}
                                />
                            </FlexCol>
                        </FlexCol>
                    </FlexCol>

                    <FlexCol flex={1} padding={2} widthPercent={100} gap={1.5} className="features-column" style={{
                            background: '#FFFFFF',
                            borderRadius: '16px',
                            border: '1px solid rgba(59, 130, 246, 0.1)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        }}>
                        <BCHeader3 text="Access everything you need" style={{ marginBottom: '0.2rem' }} />
                        
                        <FlexCol gap={1}>
                            {[
                                'Become a career-ready developer',
                                'Simplify the job search and land more interviews',
                                'Practice coding interviews and learn how to ace them',
                                'Get support from a friendly dev community'
                            ].map((feature, idx) => (
                                <FlexRow key={idx} align="center" gap={0.8}>
                                    <div style={{
                                        width: '20px',
                                        height: '20px',
                                        borderRadius: '50%',
                                        background: 'var(--color-primary)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0
                                    }}>
                                        <BCIcon 
                                            icon={ICONS.CHECKMARK}
                                            style={{ color: 'white', padding: 0, margin: 0}}
                                        />
                                    </div>
                                    <Body1 text={feature} style={{ fontSize: '1rem' }} />
                                </FlexRow>
                            ))}
                        </FlexCol>

                        <div style={{background: COLORS.BORDER_LIGHT, width: '90%', height: '1px', alignSelf: "center", margin: '.5em 0'}} />

                        <FlexCol 
                            padding={1.5} 
                            style={{
                                background: 'rgba(255, 255, 255, 1)',
                                borderRadius: '12px',
                                position: 'relative',
                                border: `1px solid ${COLORS.ACCENT_300}`
                            }}
                            gap={1}
                        >
                            <FlexRow 
                                align="center" 
                                gap={0.2} 
                                style={{ 
                                    position: 'absolute', 
                                    top: '12px', 
                                    right: '12px' 
                                }}
                            >
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <BCIcon 
                                        key={star}
                                        icon={ICONS.RATING_STAR} 
                                        style={{ 
                                            color: '#fbbf24', 
                                            fontSize: '0.8em' 
                                        }} 
                                    />
                                ))}
                            </FlexRow>

                            <FlexRow align="center" gap={1}>
                                <img 
                                    src={TESTIMONIALS_CONFIG.items[0].avatar}
                                    alt="Ryan T."
                                    style={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '50%',
                                        objectFit: 'cover'
                                    }}
                                    onError={(e) => {
                                        // Fallback to placeholder if image fails to load
                                        const target = e.target as HTMLImageElement;
                                        target.style.background = '#ddd';
                                        target.style.backgroundImage = 'url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNFNUU3RUIiLz4KPHBhdGggZD0iTTIwIDEwQzE2LjY4NjMgMTAgMTQgMTIuNjg2MyAxNCAxNkMxNCAxOS4zMTM3IDE2LjY4NjMgMjIgMjAgMjJDMjMuMzEzNyAyMiAyNiAxOS4zMTM3IDI2IDE2QzI2IDEyLjY4NjMgMjMuMzEzNyAxMCAyMCAxMFoiIGZpbGw9IiM5Q0EzQUYiLz4KPHBhdGggZD0iTTIwIDI0QzE1LjU4MTcgMjQgMTIgMjcuNTgxNyAxMiAzMlYzNEMxMiAzNC41NTIzIDEyLjQ0NzcgMzUgMTMgMzVIMjdDMjcuNTUyMyAzNSAyOCAzNC41NTIzIDI4IDM0VjMyQzI4IDI3LjU4MTcgMjQuNDE4MyAyNCAyMCAyNFoiIGZpbGw9IiM5Q0EzQUYiLz4KPC9zdmc+)';
                                        target.src = '';
                                    }}
                                />
                                <FlexCol gap={0.1}>
                                    <Body1 text="Ryan T." fontWeight="semiBold" fontSize={0.9} />
                                    <Body1 text="Web Developer" fontSize={0.8} color="TEXT_SECONDARY" />
                                </FlexCol>
                            </FlexRow>
                            <Body1 
                                text='"Beyond Code has been an invaluable tool at helping me prepare me for a job in the tech industry"' 
                                style={{ 
                                    fontStyle: 'italic', 
                                    fontSize: '0.9rem',
                                    lineHeight: '1.4'
                                }}
                            />
                        </FlexCol>
                    </FlexCol>
                </FlexRow>
            )}
        </FlexCol>
    );

    if (showAsModal && onClose) {
        return (
            <>
                {/* Backdrop */}
                <div 
                    onClick={onClose}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 1000,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                />
                
                {/* Modal */}
                <FlexCol
                    style={{
                        position: 'fixed',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        background: 'white',
                        borderRadius: '16px',
                        boxShadow: '0 20px 25px rgba(0, 0, 0, 0.3)',
                        zIndex: 1001,
                        maxWidth: '90vw',
                        maxHeight: '90vh',
                        overflow: 'auto',
                        padding: '2rem'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Close button */}
                    <BCIcon 
                        icon={ICONS.CLOSE} 
                        onClick={onClose}
                        style={{
                            position: 'absolute',
                            top: '1rem',
                            right: '1rem',
                            cursor: 'pointer',
                            fontSize: '1.5rem',
                            color: COLORS.TEXT_SECONDARY,
                            zIndex: 2
                        }}
                    />
                    {content}
                </FlexCol>
            </>
        );
    }

    return content;
};

// Pricing Option Card Component
const PricingOptionCard: React.FC<{
    title: string;
    subtitle: string;
    price: string;
    isSelected: boolean;
    onClick: () => void;
    badge: string | null;
}> = ({ title, subtitle, price, isSelected, onClick, badge }) => {
    return (
        <FlexCol
            padding={1.5}
            borderRadius={1}
            style={{
                background: 'white',
                border: isSelected ? '2px solid var(--color-primary)' : '2px solid #e9ecef',
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                position: 'relative',
                boxShadow: isSelected ? '0 6px 20px rgba(59, 130, 246, 0.2)' : '0 2px 8px rgba(0,0,0,0.08)'
            }}
            onClick={onClick}
            gap={0.8}
        >
            {/* Badge for yearly */}
            {badge && (
                <div style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: '#ea580c',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    fontWeight: '600'
                }}>
                    {badge}
                </div>
            )}
            
            <FlexRow align="center" gap={1}>
                <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    border: `2px solid ${isSelected ? 'var(--color-primary)' : '#9ca3af'}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: isSelected ? 'var(--color-primary)' : 'white'
                }}>
                    {isSelected && (
                        <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: 'white'
                        }} />
                    )}
                </div>
                <BCHeader3 text={title} style={{ color: isSelected ? 'var(--color-primary)' : '#333' }} />
            </FlexRow>
            
            <FlexCol gap={0.2} style={{ marginLeft: '2rem' }}>
                <BCHeader2 
                    text={subtitle} 
                    style={{ 
                        fontSize: '1.3rem', 
                        fontWeight: '600',
                        color: '#1f2937'
                    }} 
                />
                <Body1 
                    text={price} 
                    fontSize={0.95} 
                    style={{ 
                        color: '#4b5563',
                        fontWeight: '500'
                    }} 
                />
            </FlexCol>
        </FlexCol>
    );
};
